import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/Button';

const mainNavigation = [
  { name: 'Home', href: '/', current: true },
  { name: 'Features', href: '/features', current: false },
  { name: 'How It Works', href: '/how-it-works', current: false },
  { name: 'Report', href: '/report', current: false },
  { name: 'Reports', href: '/reports', current: false },
  { name: 'FAQ', href: '/faq', current: false },
  { name: 'Contact', href: '/contact', current: false },
];

const featureLinks = [
  { name: 'Clinical Microbiology', href: '/clinical-microbiology', current: false },
  { name: 'Antimicrobial Resistance', href: '/antimicrobial-resistance', current: false },
  { name: 'Treatment Recommendations', href: '/treatment-recommendations', current: false },
  { name: 'Complexity Assessment', href: '/complexity-assessment', current: false },
  { name: 'Monitoring Plans', href: '/monitoring-plans', current: false },
  { name: 'Interactive Reporting', href: '/interactive-reporting', current: false },
  { name: 'PGx Platform', href: 'https://pgx.reasonshield.com', current: false, isExternal: true },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

// Mobile menu component that handles its own state
const MobileMenu: React.FC<{
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  navigation: typeof mainNavigation;
  featureLinks: typeof featureLinks;
}> = ({ isOpen, setIsOpen, navigation, featureLinks }) => {
  if (!isOpen) return null;
  
  return (
    <div className="sm:hidden px-2 pb-3 pt-2 space-y-1">
      {navigation.map((item) => {
        if (item.name === 'Features') {
          return (
            <div key={item.name}>
              <a 
                href={item.href}
                className={classNames(
                  item.current
                    ? 'bg-background-alt text-primary'
                    : 'text-text hover:bg-background-alt hover:text-primary',
                  'block rounded-md px-3 py-2 text-base font-medium'
                )}
                aria-current={item.current ? 'page' : undefined}
                onClick={() => setIsOpen(false)}
              >
                {item.name}
              </a>
              {featureLinks.map((featureItem) => (
                (featureItem as any).isExternal ? (
                  <a
                    key={featureItem.name}
                    href={featureItem.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classNames(
                      featureItem.current
                        ? 'bg-background-alt text-primary'
                        : 'text-text hover:bg-background-alt hover:text-primary',
                      'block rounded-md px-3 py-2 pl-6 text-sm font-medium'
                    )}
                    onClick={() => setIsOpen(false)}
                  >
                    {featureItem.name}
                  </a>
                ) : (
                  <Link
                    key={featureItem.name}
                    to={featureItem.href}
                    className={classNames(
                      featureItem.current
                        ? 'bg-background-alt text-primary'
                        : 'text-text hover:bg-background-alt hover:text-primary',
                      'block rounded-md px-3 py-2 pl-6 text-sm font-medium'
                    )}
                    onClick={() => setIsOpen(false)}
                  >
                    {featureItem.name}
                  </Link>
                )
              ))}
            </div>
          );
        }
        return (item as any).isExternal ? (
          <a
            key={item.name}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            className={classNames(
              item.current
                ? 'bg-background-alt text-primary'
                : 'text-text hover:bg-background-alt hover:text-primary',
              'block rounded-md px-3 py-2 text-base font-medium'
            )}
            aria-current={item.current ? 'page' : undefined}
            onClick={() => setIsOpen(false)}
          >
            {item.name}
          </a>
        ) : (
          <Link
            key={item.name}
            to={item.href}
            className={classNames(
              item.current
                ? 'bg-background-alt text-primary'
                : 'text-text hover:bg-background-alt hover:text-primary',
              'block rounded-md px-3 py-2 text-base font-medium'
            )}
            aria-current={item.current ? 'page' : undefined}
            onClick={() => setIsOpen(false)}
          >
            {item.name}
          </Link>
        );
      })}
      
      <div className="pt-4 pb-2">
        <div className="border-t border-border pt-4 flex flex-col space-y-2">
          <Link 
            to="/contact"
            className="w-full block text-center rounded-md bg-primary text-text-light py-2 px-3 text-base font-medium"
            onClick={() => setIsOpen(false)}
          >
            Schedule a Demo
          </Link>
          <a 
            href="https://app.reasonshield.com/vendor-portal" 
            target="_blank" 
            rel="noopener noreferrer"
            className="w-full block text-center rounded-md bg-secondary text-text-light py-2 px-3 text-base font-medium"
            onClick={() => setIsOpen(false)}
          >
            Vendor Login
          </a>
        </div>
      </div>
    </div>
  );
};

const Header: React.FC = () => {
  const [featuresDropdownOpen, setFeaturesDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const toggleFeaturesDropdown = () => {
    setFeaturesDropdownOpen(!featuresDropdownOpen);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Update current state based on location
  const updatedMainNav = mainNavigation.map(item => ({
    ...item,
    current: item.href === location.pathname
  }));

  const updatedFeatureLinks = featureLinks.map(item => ({
    ...item,
    current: item.href === location.pathname
  }));

  return (
    <nav className="bg-background shadow fixed top-0 left-0 right-0 z-50">
      <div className="container-custom">
        <div className="relative flex h-16 items-center justify-between">
          <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
            {/* Mobile menu button*/}
            <button 
              className="inline-flex items-center justify-center rounded-md p-2 text-text hover:bg-background-alt hover:text-primary focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
              onClick={toggleMobileMenu}
            >
              <span className="sr-only">Open main menu</span>
              {mobileMenuOpen ? (
                <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
          <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
            <div className="flex flex-shrink-0 items-center">
              <Link to="/">
                <img
                  className="block h-8 w-auto"
                  src="/fulllogo_nobuffer.png"
                  alt="ReasonShield"
                />
              </Link>
            </div>
            <div className="hidden sm:ml-6 sm:block">
              <div className="flex space-x-4">
                {updatedMainNav.map((item) => {
                  if (item.name === 'Features') {
                    return (
                      <div key={item.name} className="relative">
                        <button
                          onClick={toggleFeaturesDropdown}
                          className={classNames(
                            'text-text hover:bg-background-alt hover:text-primary',
                            'rounded-md px-3 py-2 text-sm font-medium flex items-center'
                          )}
                        >
                          {item.name}
                          <ChevronDownIcon className="h-4 w-4 ml-1" aria-hidden="true" />
                        </button>
                        {featuresDropdownOpen && (
                          <div className="absolute left-0 z-10 mt-2 w-56 origin-top-left rounded-md bg-background shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                            <div className="py-1">
                              {updatedFeatureLinks.map((featureItem) => (
                                (featureItem as any).isExternal ? (
                                  <a
                                    key={featureItem.name}
                                    href={featureItem.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={classNames(
                                      featureItem.current
                                        ? 'bg-background-alt text-primary'
                                        : 'text-text hover:bg-background-alt hover:text-primary',
                                      'block px-4 py-2 text-sm'
                                    )}
                                    onClick={() => setFeaturesDropdownOpen(false)}
                                  >
                                    {featureItem.name}
                                  </a>
                                ) : (
                                  <Link
                                    key={featureItem.name}
                                    to={featureItem.href}
                                    className={classNames(
                                      featureItem.current
                                        ? 'bg-background-alt text-primary'
                                        : 'text-text hover:bg-background-alt hover:text-primary',
                                      'block px-4 py-2 text-sm'
                                    )}
                                    onClick={() => setFeaturesDropdownOpen(false)}
                                  >
                                    {featureItem.name}
                                  </Link>
                                )
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  }
                  return (item as any).isExternal ? (
                    <a
                      key={item.name}
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classNames(
                        item.current
                          ? 'bg-background-alt text-primary'
                          : 'text-text hover:bg-background-alt hover:text-primary',
                        'rounded-md px-3 py-2 text-sm font-medium'
                      )}
                      aria-current={item.current ? 'page' : undefined}
                    >
                      {item.name}
                    </a>
                  ) : (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={classNames(
                        item.current
                          ? 'bg-background-alt text-primary'
                          : 'text-text hover:bg-background-alt hover:text-primary',
                        'rounded-md px-3 py-2 text-sm font-medium'
                      )}
                      aria-current={item.current ? 'page' : undefined}
                    >
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="hidden sm:flex items-center sm:static sm:inset-auto sm:ml-6 sm:pr-0">
            <Button
              href="/contact"
              variant="primary"
              size="sm"
            >
              Schedule a Demo
            </Button>
            <Button
              href="https://app.reasonshield.com/vendor-portal"
              variant="secondary"
              size="sm"
              isExternal
              className="ml-2"
            >
              Vendor Login
            </Button>
          </div>
        </div>
      </div>
      
      {/* Mobile menu */}
      <MobileMenu 
        isOpen={mobileMenuOpen} 
        setIsOpen={setMobileMenuOpen}
        navigation={updatedMainNav}
        featureLinks={updatedFeatureLinks}
      />
    </nav>
  );
};

export default Header; 